import { Minus, Plus, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { ConfigComponent } from "./types";

interface ActiveComponentCardProps {
  component: ConfigComponent;
  instanceLabel?: string;
  onRemove: () => void;
  onQuantityChange: (quantity: number) => void;
  onMaterialChange: (materialId: string) => void;
  onDimensionChange: (dimension: 'width' | 'height' | 'thickness', value: number | null) => void;
  onAttributeChange: (attributeId: string, value: string | number | null) => void;
}

export function ActiveComponentCard({
  component,
  instanceLabel,
  onRemove,
  onQuantityChange,
  onMaterialChange,
  onDimensionChange,
  onAttributeChange
}: ActiveComponentCardProps) {
  const materialName = component.allowedMaterials.find(m => m.id === component.materialId)?.name;

  return (
    <Card className="border-l-4 border-l-primary">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 min-w-0">
            <CardTitle className="text-base truncate">{instanceLabel || component.componentTypeLabel}</CardTitle>
            {component.sourceModelName && (
              <Badge variant="secondary" className="text-xs shrink-0 bg-blue-50 text-blue-700 border-blue-200">
                {component.sourceModelName}
              </Badge>
            )}
            {materialName && (
              <Badge variant="outline" className="text-xs shrink-0">
                {materialName}
              </Badge>
            )}
          </div>
          
          <div className="flex items-center gap-2 shrink-0">
            {/* Quantity controls */}
            <div className="flex items-center border rounded-md">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => onQuantityChange(component.quantity - 1)}
                disabled={component.quantity <= 1}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="w-8 text-center text-sm font-medium">{component.quantity}</span>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => onQuantityChange(component.quantity + 1)}
                disabled={component.quantity >= component.maxQuantity}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Remove button */}
            <Button 
              variant="ghost" 
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={onRemove}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0 pb-4 space-y-4">
        {/* Material Selection */}
        {component.allowedMaterials.length > 0 && (
          <div className="space-y-2">
            <Label className="text-sm">Materiaali</Label>
            <Select
              value={component.materialId || ""}
              onValueChange={onMaterialChange}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Valitse materiaali" />
              </SelectTrigger>
              <SelectContent>
                {component.allowedMaterials.map((material) => (
                  <SelectItem key={material.id} value={material.id}>
                    {material.name}
                    {material.isDefault && (
                      <span className="text-xs text-muted-foreground ml-2">(oletus)</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Dimensions - only for 'free' mode */}
        {component.dimensionMode === 'free' && (
          <div className="space-y-2">
            <Label className="text-sm">Mitat (mm)</Label>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Leveys</Label>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  placeholder="-"
                  className="h-9"
                  value={component.width != null ? Math.round(component.width * 1000) : ""}
                  onChange={(e) =>
                    onDimensionChange('width', e.target.value ? parseFloat(e.target.value) / 1000 : null)
                  }
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Korkeus</Label>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  placeholder="-"
                  className="h-9"
                  value={component.height != null ? Math.round(component.height * 1000) : ""}
                  onChange={(e) =>
                    onDimensionChange('height', e.target.value ? parseFloat(e.target.value) / 1000 : null)
                  }
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Paksuus</Label>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  placeholder="-"
                  className="h-9"
                  value={component.thickness != null ? Math.round(component.thickness * 1000) : ""}
                  onChange={(e) =>
                    onDimensionChange('thickness', e.target.value ? parseFloat(e.target.value) / 1000 : null)
                  }
                />
              </div>
            </div>
          </div>
        )}

        {/* Catalog mode notice */}
        {component.dimensionMode === 'catalog' && (
          <p className="text-xs text-muted-foreground italic">
            Koko määräytyy attribuuteista
          </p>
        )}

        {/* Attributes */}
        {component.attributes.length > 0 && (
          <div className="space-y-3 pt-2 border-t">
            <Label className="text-sm font-medium text-muted-foreground">Attribuutit</Label>
            {component.attributes.map((attr) => (
              <div key={attr.id} className="space-y-1">
                <Label className="text-sm">
                  {attr.label}
                  {attr.isRequired && <span className="text-destructive ml-1">*</span>}
                </Label>
                {renderAttributeInput(attr, (value) => onAttributeChange(attr.id, value))}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Helper to render attribute inputs
function renderAttributeInput(
  attr: ConfigComponent['attributes'][0],
  onChange: (value: string | number | null) => void
) {
  switch (attr.type) {
    case 'text':
      return (
        <Input
          value={(attr.value as string) || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={attr.label}
          className="h-9"
        />
      );
    case 'number':
      return (
        <Input
          type="number"
          value={(attr.value as number) || ""}
          onChange={(e) => onChange(e.target.value ? parseFloat(e.target.value) : null)}
          placeholder={attr.label}
          className="h-9"
        />
      );
    case 'date':
      return (
        <Input
          type="date"
          value={(attr.value as string) || ""}
          onChange={(e) => onChange(e.target.value)}
          className="h-9"
        />
      );
    case 'selection':
      return (
        <Select
          value={(attr.value as string) || ""}
          onValueChange={onChange}
        >
          <SelectTrigger className="h-9">
            <SelectValue placeholder={`Valitse ${attr.label.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {attr.options?.map((option) => {
              const optLabel = typeof option === 'string' ? option : (option?.label || String(option));
              const optValue = typeof option === 'string' ? option : (option?.value || String(option));
              return (
                <SelectItem key={optValue} value={optValue}>
                  {optLabel}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      );
    default:
      return (
        <Input
          value={(attr.value as string) || ""}
          onChange={(e) => onChange(e.target.value)}
          className="h-9"
        />
      );
  }
}
